import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

/**
 * Setup phases mirror the Aria One mock — but only the steps that have
 * real API counterparts are "live"; the rest stay visible as locked previews.
 */
export const SETUP_PHASES = [
  {
    id: "account",
    name: "Your Account",
    steps: [
      { id: "account", name: "Account & profile", live: true },
      { id: "profile", name: "Practice profile & Tax ID", live: false },
      { id: "providers", name: "Providers · NPI · CAQH", live: true },
      { id: "clients", name: "Clients (4-digit IDs)", live: true },
      { id: "link", name: "Link providers ↔ clients", live: true },
      { id: "locations", name: "Locations & place of service", live: false },
      { id: "ehr", name: "EHR / PM connection", live: false },
    ],
  },
  {
    id: "connections",
    name: "Connections & Enrollment",
    steps: [
      { id: "clearing", name: "Clearinghouse · EDI", live: false },
      { id: "era", name: "ERA / EFT enrollment", live: false },
      { id: "payers", name: "Payer list", live: false },
      { id: "fees", name: "Fee schedules", live: false },
    ],
  },
  {
    id: "data",
    name: "Your Data",
    steps: [
      { id: "history", name: "Claim history import", live: false },
      { id: "balances", name: "Patient balances / A·R", live: false },
      { id: "records", name: "Medical records access", live: false },
    ],
  },
  {
    id: "rules",
    name: "Billing Rules",
    steps: [
      { id: "rules", name: "Practice preferences", live: false },
      { id: "auth", name: "Authorizations & adjustments", live: false },
      { id: "modifiers", name: "Modifier permissions", live: false },
      { id: "statements", name: "Patient statements & self-pay", live: false },
      { id: "signoff", name: "Review & authorize", live: false },
    ],
  },
];

export function flatSteps() {
  return SETUP_PHASES.flatMap((p) => p.steps.map((s) => ({ ...s, phase: p.id })));
}

/**
 * Drives the progress ring + "X of Y steps" copy.  We *only* mark a step complete
 * when the live API confirms data exists for it; mocked steps stay incomplete.
 */
export function useSetupProgress() {
  const { data: clients } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => (await api.get("/api/edi/note-to-claim/clients")).data?.clients ?? [],
    staleTime: 30_000,
  });
  const { data: providers } = useQuery({
    queryKey: ["providers"],
    queryFn: async () => (await api.get("/api/edi/note-to-claim/providers")).data?.providers ?? [],
    staleTime: 30_000,
  });
  const { data: profile } = useQuery({
    queryKey: ["profile"],
    queryFn: async () => (await api.get("/api/ehibridge/auth/profile")).data,
    staleTime: 60_000,
  });

  const completion = {
    account: !!profile?.id,
    profile: false,
    providers: (providers?.length || 0) > 0,
    clients: (clients?.length || 0) > 0,
    link: (providers || []).some((p) => p.client_id || p.client_4digit_id),
    locations: false,
    ehr: false,
    clearing: false,
    era: false,
    payers: false,
    fees: false,
    history: false,
    balances: false,
    records: false,
    rules: false,
    auth: false,
    modifiers: false,
    statements: false,
    signoff: false,
  };

  const all = flatSteps();
  const total = all.length;
  const completed = all.filter((s) => completion[s.id]).length;
  const percent = total ? Math.round((completed / total) * 100) : 0;

  return { completion, completed, total, percent, profile, clients: clients || [], providers: providers || [] };
}
